import { useSyncExternalStore } from "react";

// Een AI-voorstel als taak die los van het scherm doorloopt — dezelfde opzet als het nabouwen van een
// document (communicatie/editor/importTaak.ts). Gebruikt door het taakvoorstel (Instellingen › Taken) en
// "Maak een wizard met AI" (Instellingen › Wizards).
//
// WAAROM. Zo'n voorstel duurt al snel een halve minuut tot een minuut. Nu start je het, werk je verder,
// en rechtsonder zie je hoe lang het al loopt; met "Bekijken" kom je terug bij het resultaat.
//
// Eén voorstel per soort tegelijk, en alleen in dit tabblad: het resultaat leeft in het geheugen van de app.

interface Basis {
  beschrijving: string;
}

export type AchtergrondTaak<R> =
  | { status: "leeg" }
  | (Basis & { status: "bezig"; gestart: number })
  | (Basis & { status: "klaar"; resultaat: R; bekijken: boolean })
  /** {@code code}: de http-status als de api weigerde (401 = sessie verlopen); {@code melding}: eigen uitleg. */
  | (Basis & { status: "fout"; code: number | null; melding: string | null });

/** Een fout met een eigen uitleg voor de melding (bijv. "de AI kon hier geen wizard van maken"). */
export class VoorstelFout extends Error {
  constructor(readonly melding: string) {
    super(melding);
  }
}

export interface AchtergrondStore<R> {
  get(): AchtergrondTaak<R>;
  subscribe(l: () => void): () => void;
  /** Start een voorstel; {@code verzend} is een gewone functie, zodat hij ook doorloopt als het scherm weg is. */
  start(beschrijving: string, verzend: (beschrijving: string) => Promise<R>): Promise<void>;
  /** Stoppen of weggooien: een antwoord dat hierna nog binnenkomt, wordt genegeerd. */
  stop(): void;
  /** "Bekijken" in de melding: het scherm dat bij dit voorstel hoort, pakt het resultaat op. */
  vraagBekijken(): void;
  /** Het scherm heeft het resultaat overgenomen; de melding verdwijnt. */
  overgenomen(): void;
  isDialoogOpen(): boolean;
  setDialoogOpen(open: boolean): void;
}

export function maakAchtergrondTaak<R>(): AchtergrondStore<R> {
  let taak: AchtergrondTaak<R> = { status: "leeg" };
  let dialoogOpen = false;
  let volgnummer = 0;
  const luisteraars = new Set<() => void>();
  const zet = (nieuw: AchtergrondTaak<R>) => {
    taak = nieuw;
    for (const l of luisteraars) l();
  };

  return {
    get: () => taak,
    subscribe(l) {
      luisteraars.add(l);
      return () => {
        luisteraars.delete(l);
      };
    },
    async start(beschrijving, verzend) {
      const mijn = ++volgnummer;
      zet({ beschrijving, status: "bezig", gestart: Date.now() });
      try {
        const resultaat = await verzend(beschrijving);
        if (mijn === volgnummer) zet({ beschrijving, status: "klaar", resultaat, bekijken: false });
      } catch (e) {
        if (mijn !== volgnummer) return;
        zet({
          beschrijving,
          status: "fout",
          code: statusVan(e),
          melding: e instanceof VoorstelFout ? e.melding : null,
        });
      }
    },
    stop() {
      volgnummer++;
      zet({ status: "leeg" });
    },
    vraagBekijken() {
      if (taak.status === "klaar") zet({ ...taak, bekijken: true });
    },
    overgenomen() {
      zet({ status: "leeg" });
    },
    isDialoogOpen: () => dialoogOpen,
    setDialoogOpen(open) {
      if (dialoogOpen === open) return;
      dialoogOpen = open;
      for (const l of luisteraars) l();
    },
  };
}

function statusVan(e: unknown): number | null {
  if (e && typeof e === "object" && "statusCode" in e) {
    const s = (e as { statusCode?: unknown }).statusCode;
    return typeof s === "number" ? s : null;
  }
  return null;
}

export function useAchtergrondTaak<R>(store: AchtergrondStore<R>): AchtergrondTaak<R> {
  return useSyncExternalStore(store.subscribe, store.get, store.get);
}

export function useAchtergrondDialoogOpen<R>(store: AchtergrondStore<R>): boolean {
  return useSyncExternalStore(store.subscribe, store.isDialoogOpen, store.isDialoogOpen);
}

/** Het AI-taakvoorstel (Instellingen › Taken): het resultaat is het concept voor de sjablooneditor. */
export const aiTaakVoorstel = maakAchtergrondTaak<Record<string, unknown>>();

/** "Maak een wizard met AI" (Instellingen › Wizards): het resultaat is het id van de nieuwe conceptwizard. */
export const aiWizardVoorstel = maakAchtergrondTaak<string>();
